import { useEffect, useState } from 'react';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhAlertsBySeverityData,
  WazuhOrganizationAttentionItem,
  WazuhPagedData,
  WazuhPortfolioHealthResellerData,
  WazuhRecentIncidentItem,
  WazuhSecurityScoreData,
  WazuhThreatCategoriesData,
  WazuhTopCveItem,
  WazuhVulnerabilitiesBySeverityData,
} from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { applyProfileResellerScope } from './wazuhScopeParams';

export type WazuhDashboardState = {
  loading: boolean;
  portfolioHealth: WazuhPortfolioHealthResellerData | null;
  alertsBySeverity: WazuhAlertsBySeverityData | null;
  threatCategories: WazuhThreatCategoriesData | null;
  vulnerabilitiesBySeverity: WazuhVulnerabilitiesBySeverityData | null;
  securityScore: WazuhSecurityScoreData | null;
  organizationsAttention: WazuhPagedData<WazuhOrganizationAttentionItem> | null;
  recentIncidents: WazuhPagedData<WazuhRecentIncidentItem> | null;
  topCves: WazuhPagedData<WazuhTopCveItem> | null;
};

const emptyDashboardState = (): WazuhDashboardState => ({
  loading: true,
  portfolioHealth: null,
  alertsBySeverity: null,
  threatCategories: null,
  vulnerabilitiesBySeverity: null,
  securityScore: null,
  organizationsAttention: null,
  recentIncidents: null,
  topCves: null,
});

export const useWazuhDashboardData = (
  attentionPage = 0,
  incidentsPage = 0,
  cvesPage = 0,
  pageSize = 5,
  incidentsPageSize = 5
): WazuhDashboardState => {
  const { scope } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<WazuhDashboardState>(emptyDashboardState);

  useEffect(() => {
    let cancelled = false;

    if (profileOrgId) {
      setState((prev) => ({ ...prev, loading: true }));

      const scopeWithReseller = applyProfileResellerScope(scope, profileOrgId);

      Promise.all([
        wazuhAPI.dashboard.getPortfolioHealthReseller(scopeWithReseller),
        wazuhAPI.dashboard.getAlertsBySeverity(scopeWithReseller),
        wazuhAPI.dashboard.getThreatCategories(scopeWithReseller),
        wazuhAPI.dashboard.getVulnerabilitiesBySeverity(scopeWithReseller),
        wazuhAPI.dashboard.getSecurityScore(scopeWithReseller),
        wazuhAPI.dashboard.getOrganizationsAttention({
          ...scopeWithReseller,
          page: attentionPage,
          pageSize,
        }),
        wazuhAPI.dashboard.getRecentIncidents({
          ...scopeWithReseller,
          page: incidentsPage,
          pageSize: incidentsPageSize,
        }),
        wazuhAPI.dashboard.getTopCves({
          ...scopeWithReseller,
          page: cvesPage,
          pageSize,
        }),
      ])
        .then(
          ([
            portfolioHealth,
            alertsBySeverity,
            threatCategories,
            vulnerabilitiesBySeverity,
            securityScore,
            organizationsAttention,
            recentIncidents,
            topCves,
          ]) => {
            if (cancelled) return;
            setState({
              loading: false,
              portfolioHealth,
              alertsBySeverity,
              threatCategories,
              vulnerabilitiesBySeverity,
              securityScore,
              organizationsAttention,
              recentIncidents,
              topCves,
            });
          }
        )
        .catch((error) => {
          if (!cancelled) {
            logger(error, 'error', 'useWazuhDashboardData');
            setState({ ...emptyDashboardState(), loading: false });
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    attentionPage,
    incidentsPage,
    cvesPage,
    pageSize,
    incidentsPageSize,
  ]);

  return state;
};
